import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SetupAssistant from "@/components/SetupAssistant";
import { useProperty } from "@/contexts/PropertyContext";
import { ReservationDrawer } from "./CalendarPage";
import {
  ArrowDown,
  ArrowUp,
  CurrencyEur,
  ChartLine,
  CalendarBlank,
  ArrowRight,
  CircleNotch,
  Copy,
  PaperPlaneTilt,
  CheckCircle,
  Broom,
} from "@phosphor-icons/react";

function StatCard({ icon: Icon, label, value, subtext, accent, testId }) {
  return (
    <div className="stat-card lift !p-3 sm:!p-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sm:text-xs">{label}</p>
        <div
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md sm:h-9 sm:w-9"
          style={{ background: `hsl(var(--${accent || "primary"}) / 0.12)`, color: `hsl(var(--${accent || "primary"}))` }}
        >
          <Icon size={18} weight="duotone" />
        </div>
      </div>
      <p className="mt-2 font-mono text-2xl font-bold text-foreground sm:mt-3 sm:text-3xl">{value}</p>
      {subtext && <p className="mt-1 truncate text-[11px] text-muted-foreground sm:text-xs">{subtext}</p>}
    </div>
  );
}

// Check-in status badge — shared between "Dolasci danas" and "Nadolazeći 7 dana".
const CHECKIN_BADGE = {
  pending:    { label: "Čeka prijavu",    cls: "bg-amber-100 text-amber-800 border-amber-300" },
  submitted:  { label: "Forma ispunjena", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  checked_in: { label: "Check-in poslan", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  cancelled:  { label: "Otkazano",        cls: "bg-rose-100 text-rose-800 border-rose-300" },
  expired:    { label: "Isteklo",         cls: "bg-slate-200 text-slate-700 border-slate-300" },
};

// =============================================================================
// Arrivals today — clickable rows + single-click eVisitor + copy link
// =============================================================================
function ArrivalsCard({ propertyId, onOpen }) {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    const params = propertyId ? `?property_id=${encodeURIComponent(propertyId)}` : "";
    api.get(`/dashboard/arrivals${params}`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);

  const copyLink = async (token, e) => {
    e.stopPropagation();
    if (!token) return;
    const url = `${window.location.origin}/check-in/r/${token}`;
    try { await navigator.clipboard.writeText(url); }
    catch {
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    setCopiedId(token);
    setTimeout(() => setCopiedId(null), 1600);
  };

  const singleClick = async (it, e) => {
    e.stopPropagation();
    if (!window.confirm(`Potvrdi check-in za ${it.primary_guest_name}? Podaci se šalju na eVisitor.`)) return;
    setBusyId(it.id);
    setMsg("");
    try {
      const { data } = await api.post(`/reservations/${it.id}/checkin`, {});
      setMsg(data.ok
        ? `✓ ${it.primary_guest_name}: ${data.submitted}/${data.total} gostiju poslano na eVisitor.`
        : `${it.primary_guest_name}: ${data.submitted}/${data.total} poslano, ${data.failed?.length || 0} grešaka.`
      );
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Greška pri slanju");
    } finally {
      setBusyId(null);
    }
  };

  if (items === null) {
    return <div className="flex h-32 items-center justify-center"><CircleNotch size={20} className="animate-spin text-muted-foreground" /></div>;
  }
  if (items.length === 0) {
    return <p className="px-5 py-8 text-center text-sm text-muted-foreground">Nema dolazaka danas</p>;
  }

  return (
    <div className="divide-y divide-border" data-testid="arrivals-card">
      {msg && <div className="border-b border-border bg-muted/40 px-5 py-2 text-xs text-foreground" data-testid="arrivals-msg">{msg}</div>}
      {items.map((it) => {
        const badge = CHECKIN_BADGE[it.checkin_status] || CHECKIN_BADGE.pending;
        const canCheckin = it.checkin_status === "submitted";
        return (
          <div
            key={it.id}
            className="cursor-pointer px-5 py-3 transition-colors hover:bg-muted/40"
            onClick={() => onOpen(it.id)}
            data-testid={`arrival-row-${it.code}`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter") onOpen(it.id); }}
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{it.primary_guest_name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {it.code} • {it.property_name || "—"} {it.unit_code ? `/ ${it.unit_code}` : ""} • {it.nights} noć(i) • {it.adults} odraslih{it.children ? `, ${it.children} djece` : ""}
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">Prijavljeno {it.guests_submitted}/{it.expected_guests} gostiju</p>
              </div>
              <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${badge.cls}`} data-testid={`arrival-checkin-badge-${it.code}`}>
                {badge.label}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={(e) => copyLink(it.checkin_token, e)}
                disabled={!it.checkin_token || it.checkin_status === "cancelled" || it.checkin_status === "expired"}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium hover:bg-muted disabled:opacity-40"
                data-testid={`arrival-copy-${it.code}`}
              >
                {copiedId === it.checkin_token ? <><CheckCircle size={12} weight="fill" className="text-emerald-500" /> Kopirano</> : <><Copy size={12} /> Kopiraj link</>}
              </button>
              <button
                type="button"
                onClick={(e) => singleClick(it, e)}
                disabled={!canCheckin || busyId === it.id}
                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                data-testid={`arrival-singleclick-${it.code}`}
              >
                {busyId === it.id ? <CircleNotch size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} weight="fill" />}
                {it.checkin_status === "checked_in" ? "Već poslano" : "Single-click"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// Departures today — new card so cleaning knows which units to prep
// =============================================================================
function DeparturesCard({ propertyId, onOpen }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    const params = propertyId ? `?property_id=${encodeURIComponent(propertyId)}` : "";
    api.get(`/dashboard/departures${params}`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [propertyId]);

  if (items === null) {
    return <div className="flex h-32 items-center justify-center"><CircleNotch size={20} className="animate-spin text-muted-foreground" /></div>;
  }
  if (items.length === 0) {
    return <p className="px-5 py-8 text-center text-sm text-muted-foreground">Nema odlazaka danas</p>;
  }

  return (
    <div className="divide-y divide-border" data-testid="departures-card">
      {items.map((it) => (
        <div
          key={it.id}
          className="cursor-pointer px-5 py-3 transition-colors hover:bg-muted/40"
          onClick={() => onOpen(it.id)}
          data-testid={`departure-row-${it.code}`}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter") onOpen(it.id); }}
        >
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{it.primary_guest_name}</p>
              <p className="truncate text-xs text-muted-foreground">
                {it.code} • {it.property_name || "—"} {it.unit_code ? `/ ${it.unit_code}` : ""} • {it.nights} noć(i)
              </p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
              <Broom size={11} weight="bold" /> Čeka čišćenje
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Upcoming (7d) — same UX pattern as ArrivalsCard: clickable + check-in badge
// =============================================================================
function UpcomingRow({ r, onOpen }) {
  const badge = CHECKIN_BADGE[r.checkin_status] || CHECKIN_BADGE.pending;
  return (
    <div
      className="cursor-pointer px-5 py-3 transition-colors hover:bg-muted/40"
      onClick={() => onOpen(r.id)}
      data-testid={`upcoming-row-${r.code}`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(r.id); }}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{r.primary_guest_name}</p>
          <p className="truncate text-xs text-muted-foreground">
            {r.code} • {r.check_in} → {r.check_out}
            {r.unit_code ? ` • ${r.unit_code}` : ""}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <p className="font-mono text-xs text-muted-foreground">€{(r.total_amount || 0).toFixed(0)}</p>
          <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${badge.cls}`} data-testid={`upcoming-checkin-badge-${r.code}`}>
            {badge.label}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { activePropertyId, activeProperty } = useProperty();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null); // { reservation, units, properties }
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    const params = activePropertyId ? `?property_id=${encodeURIComponent(activePropertyId)}` : "";
    api
      .get(`/dashboard/today${params}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [activePropertyId, refreshTick]);

  // Open the full reservation drawer given only the reservation id.
  // We need units+properties to render the drawer, so we fetch them alongside
  // the reservation on demand (cheap, both endpoints return small payloads).
  const openReservation = useCallback(async (reservationId) => {
    setDrawerLoading(true);
    try {
      const [rRes, unitsRes, propsRes] = await Promise.all([
        api.get(`/reservations/${reservationId}`),
        api.get("/units", { params: { include_inactive: true } }),
        api.get("/properties"),
      ]);
      setDrawer({ reservation: rRes.data, units: unitsRes.data || [], properties: propsRes.data || [] });
    } catch (e) {
      console.error("openReservation failed:", e);
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <CircleNotch size={28} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return <div className="text-muted-foreground">Nema podataka za prikaz.</div>;
  }

  const today = new Date(data.date).toLocaleDateString("hr-HR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <SetupAssistant />

      <div>
        <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">
          {activeProperty ? activeProperty.name : "Svi smještaji"} · Danas
        </p>
        <h2 className="mt-1 text-2xl font-bold text-foreground sm:text-3xl">{today}</h2>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard icon={ArrowDown} label="Dolasci danas" value={data.arrivals_count} subtext={`${data.in_house_count} gostiju u objektu`} accent="primary" testId="stat-arrivals" />
        <StatCard icon={ArrowUp} label="Odlasci danas" value={data.departures_count} subtext="Treba čišćenje/inspect" accent="accent" testId="stat-departures" />
        <StatCard icon={ChartLine} label="Popunjenost danas" value={`${data.occupancy_today}%`} subtext={`${data.in_house_count} od ${data.total_units} jedinica`} accent="success" testId="stat-occupancy" />
        <StatCard icon={CurrencyEur} label="Prihod ovaj mjesec" value={`€${(data.revenue_this_month || 0).toLocaleString("hr-HR", { maximumFractionDigits: 0 })}`} subtext="Potvrđene rezervacije" accent="primary" testId="stat-revenue" />
      </div>

      {/* Three-column arrivals/departures/upcoming lists */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Arrivals today */}
        <div className="rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <h3 className="text-sm font-semibold text-foreground">Dolasci danas</h3>
            <Link to="/calendar" className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid="link-calendar-arrivals">
              Otvori kalendar <ArrowRight size={12} />
            </Link>
          </div>
          <ArrivalsCard propertyId={activePropertyId} onOpen={openReservation} />
        </div>

        {/* Departures today */}
        <div className="rounded-lg border border-border bg-card" data-testid="departures-column">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <h3 className="text-sm font-semibold text-foreground">Odlasci danas</h3>
            <Broom size={16} className="text-muted-foreground" />
          </div>
          <DeparturesCard propertyId={activePropertyId} onOpen={openReservation} />
        </div>

        {/* Upcoming 7d */}
        <div className="rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <h3 className="text-sm font-semibold text-foreground">Nadolazeći dolasci (7 dana)</h3>
            <CalendarBlank size={16} className="text-muted-foreground" />
          </div>
          <div className="divide-y divide-border">
            {data.upcoming_arrivals_7d.length === 0 && (
              <p className="px-5 py-8 text-center text-sm text-muted-foreground">Nema dolazaka u sljedećih 7 dana</p>
            )}
            {data.upcoming_arrivals_7d.slice(0, 6).map((r) => (
              <UpcomingRow key={r.id} r={r} onOpen={openReservation} />
            ))}
          </div>
        </div>
      </div>

      {/* Source breakdown */}
      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="mb-4 text-sm font-semibold text-foreground">Kanali u ovom mjesecu</h3>
        {data.source_breakdown.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nema podataka o rezervacijama ovaj mjesec. Kreirajte rezervaciju u <Link to="/calendar" className="text-primary hover:underline">Kalendaru</Link>.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {data.source_breakdown.map((s) => (
              <div key={s.source} className="rounded-md border border-border p-3">
                <p className="text-xs uppercase text-muted-foreground">{s.source}</p>
                <p className="mt-1 font-mono text-lg font-bold text-foreground">{s.count}</p>
                <p className="text-xs text-muted-foreground">€{(s.revenue || 0).toFixed(0)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Loading overlay while fetching reservation on-demand */}
      {drawerLoading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="dashboard-drawer-loading">
          <CircleNotch size={28} className="animate-spin text-white" />
        </div>
      )}

      {/* Reservation drawer (shared with Calendar) */}
      {drawer && (
        <ReservationDrawer
          reservation={drawer.reservation}
          units={drawer.units}
          properties={drawer.properties}
          onClose={() => setDrawer(null)}
          onSaved={() => { setDrawer(null); setRefreshTick((t) => t + 1); }}
          onDeleted={() => { setDrawer(null); setRefreshTick((t) => t + 1); }}
        />
      )}
    </div>
  );
}
